import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { TextFormat, Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './product-process.reducer';

export const ProductProcess = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(
    overrideSortStateWithQueryParams(getSortState(pageLocation, 'id'), pageLocation.search),
  );

  const productProcessList = useAppSelector(state => state.productProcess.entities);
  const loading = useAppSelector(state => state.productProcess.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="product-process-heading" data-cy="ProductProcessHeading">
        <Translate contentKey="crawlerBackendApp.productProcess.home.title">Product Processes</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="crawlerBackendApp.productProcess.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/product-process/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="crawlerBackendApp.productProcess.home.createLabel">
              Create new Product Process
            </Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {productProcessList && productProcessList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('processType')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.processType">Process Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('processType')} />
                </th>
                <th className="hand" onClick={sort('stepType')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.stepType">Step Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('stepType')} />
                </th>
                <th className="hand" onClick={sort('stepStatus')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.stepStatus">Step Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('stepStatus')} />
                </th>
                <th className="hand" onClick={sort('retryCount')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.retryCount">Retry Count</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('retryCount')} />
                </th>
                <th className="hand" onClick={sort('payloadIn')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.payloadIn">Payload In</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('payloadIn')} />
                </th>
                <th className="hand" onClick={sort('payloadOut')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.payloadOut">Payload Out</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('payloadOut')} />
                </th>
                <th className="hand" onClick={sort('coolingOffEnd')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.coolingOffEnd">Cooling Off End</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('coolingOffEnd')} />
                </th>
                <th className="hand" onClick={sort('offerExpirationDate')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.offerExpirationDate">
                    Offer Expiration Date
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('offerExpirationDate')} />
                </th>
                <th className="hand" onClick={sort('mandateSignedDate')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.mandateSignedDate">
                    Mandate Signed Date
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('mandateSignedDate')} />
                </th>
                <th className="hand" onClick={sort('desiredDate')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.desiredDate">Desired Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('desiredDate')} />
                </th>
                <th className="hand" onClick={sort('actualStartDate')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.actualStartDate">Actual Start Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('actualStartDate')} />
                </th>
                <th className="hand" onClick={sort('transactionId')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.transactionId">Transaction Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('transactionId')} />
                </th>
                <th className="hand" onClick={sort('waiveCoolingOffRight')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.waiveCoolingOffRight">
                    Waive Cooling Off Right
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('waiveCoolingOffRight')} />
                </th>
                <th className="hand" onClick={sort('depositRequired')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.depositRequired">Deposit Required</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('depositRequired')} />
                </th>
                <th className="hand" onClick={sort('deposit')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.deposit">Deposit</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('deposit')} />
                </th>
                <th className="hand" onClick={sort('createdAt')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.createdAt">Created At</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdAt')} />
                </th>
                <th className="hand" onClick={sort('updatedAt')}>
                  <Translate contentKey="crawlerBackendApp.productProcess.updatedAt">Updated At</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('updatedAt')} />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.productProcess.product">Product</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {productProcessList.map((productProcess, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/product-process/${productProcess.id}`} color="link" size="sm">
                      {productProcess.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.ProcessType.${productProcess.processType}`} />
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.StepType.${productProcess.stepType}`} />
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.StepStatus.${productProcess.stepStatus}`} />
                  </td>
                  <td>{productProcess.retryCount}</td>
                  <td>{productProcess.payloadIn}</td>
                  <td>{productProcess.payloadOut}</td>
                  <td>
                    {productProcess.coolingOffEnd ? (
                      <TextFormat type="date" value={productProcess.coolingOffEnd} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {productProcess.offerExpirationDate ? (
                      <TextFormat
                        type="date"
                        value={productProcess.offerExpirationDate}
                        format={APP_LOCAL_DATE_FORMAT}
                      />
                    ) : null}
                  </td>
                  <td>
                    {productProcess.mandateSignedDate ? (
                      <TextFormat type="date" value={productProcess.mandateSignedDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {productProcess.desiredDate ? (
                      <TextFormat type="date" value={productProcess.desiredDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {productProcess.actualStartDate ? (
                      <TextFormat type="date" value={productProcess.actualStartDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>{productProcess.transactionId}</td>
                  <td>{productProcess.waiveCoolingOffRight ? 'true' : 'false'}</td>
                  <td>{productProcess.depositRequired ? 'true' : 'false'}</td>
                  <td>{productProcess.deposit ? 'true' : 'false'}</td>
                  <td>
                    {productProcess.createdAt ? (
                      <TextFormat type="date" value={productProcess.createdAt} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {productProcess.updatedAt ? (
                      <TextFormat type="date" value={productProcess.updatedAt} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {productProcess.product ? (
                      <Link to={`/product/${productProcess.product.id}`}>{productProcess.product.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/product-process/${productProcess.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/product-process/${productProcess.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() => (window.location.href = `/product-process/${productProcess.id}/delete`)}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="crawlerBackendApp.productProcess.home.notFound">
                No Product Processes found
              </Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ProductProcess;
